import { useEffect, useState } from 'react';
import EPageHeader from '@/components/everridge/EPageHeader';
import DashboardApartmentsFilter from '@/features/dashboard/components/DashboardApartmentsFilter';
import DashboardChart from '@/features/dashboard/components/DashboardChart/DashboardChart';
import { DashboardRecentActivities } from '@/features/dashboard/components/DashboardRecentActivities';
import DashboardNewsAndOffers from '@/features/dashboard/components/DashboardNewsAndOffers/DashboardNewsAndOffers';
import { useDashboardMainApi } from '@/features/dashboard/hooks/useDashboardMainApi';
import DashboardManagedByBlock from '@/features/dashboard/components/DashboardManagedBy/DashboardManagedByBlock';
import DashboardTotalApartmentsBlock from '@/features/dashboard/components/DashboardTotalApartments/DashboardTotalApartmentsBlock';
import DashboardTotalOwnersBlock from '@/features/dashboard/components/DashboardTotalOwners/DashboardTotalOwnersBlock';
import useMediaQuery from '@/hooks/useMediaQuery';
import { AppConfig } from '@/config/app.config';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import useEmblaCarousel from 'embla-carousel-react';
import DollarCircleIcon from '@/components/icons/DollarCircleIcon';
import ChartBarLineIcon from '@/components/icons/ChartBarLineIcon';
import CalendarAdd_01_Icon from '@/components/icons/CalendarAdd_01_Icon';
import DoorIcon from '@/components/icons/DoorIcon';
import SettingsIcon from '@/components/icons/SettingsIcon';
import User_02_Icon from '@/components/icons/User_02_Icon';
import { FilterHelper } from '@/utils/filterHelper';
import CalendarIcon from '@/components/icons/CalendarIcon';
import DashboardPageFilter from '@/features/dashboard/components/DashboardPageFilter';
import { useDashboardFilter } from '@/features/dashboard/hooks/useDashboardFilter';
import { useUserRole } from '@/features/identity/hooks/useUserRole.ts';
import { Roles } from '@/@types/auth.types.ts';
import { t } from 'i18next';
import ReleaseNews from '../releases/components/ReleaseNews';

export enum dashboardChartsButton {
	Income,
	ADR,
	OccupancyRate,
	TotalApartments,
	ManagedBy,
	TotalOwners,
}

const dashboardChartsButtonsList = [
	{
		name: 'main.dashboard.components.chart.income',
		icon: DollarCircleIcon,
	},
	{
		name: 'main.dashboard.components.chart.adr',
		icon: CalendarAdd_01_Icon,
	},
	{
		name: 'main.dashboard.components.chart.occupancyRate',
		icon: ChartBarLineIcon,
	},
	{
		name: 'main.dashboard.components.chart.totalApartments.title',
		icon: DoorIcon,
	},
	{
		name: 'main.dashboard.components.chart.managedBy.title',
		icon: SettingsIcon,
	},
	{
		name: 'main.dashboard.components.chart.totalOwners.title',
		icon: User_02_Icon,
	},
];

const DashboardPage = () => {
	const [activeChart, setActiveChart] = useState(
		dashboardChartsButton.Income,
	);
	const [showRangeFilter, setShowRangeFilter] = useState(false);

	const isMobile = useMediaQuery(AppConfig.mediaQueries.mobile);
	const userRole = useUserRole();
	const { filter, resetFilter } = useDashboardFilter();

	const {
		incomeData,
		isLoadingIncomeData,
		ADRData,
		isLoadingADRData,
		occupancyRateData,
		isLoadingOccupancyRateData,
	} = useDashboardMainApi();

	const [emblaRef, emblaApi] = useEmblaCarousel({
		align: 'center',
		dragFree: true,
	});

	useEffect(() => {
		resetFilter();
	}, []);

	const handleChooseChart = (index: number) => {
		setActiveChart(index);
		if (emblaApi) {
			emblaApi.scrollTo(index);
		}
	};

	const chartsButtons =
		userRole === Roles.admin
			? dashboardChartsButtonsList
			: dashboardChartsButtonsList.slice(0, 3);

	const filtersQuantity = FilterHelper.countFilterValues(filter, [
		'defaultStartDate',
		'defaultEndDate',
		'apartmentId',
	]);
	
	return (
		<section
			className={cn('flex-1 flex flex-col gap-6 animate-page-fade-in', isMobile && 'gap-4')}>
			<EPageHeader title={t('main.dashboard.title')}>
				{!isMobile && <DashboardApartmentsFilter />}
				<div className='relative'>
					{!!filtersQuantity && (
						<div className='absolute -top-1 right-0 size-4 rounded-full bg-error-1000 flex items-center justify-center text-2xs text-popover-10 animate-pulse '>
							{filtersQuantity}
						</div>
					)}
					<Button
						variant='outline'
						size={isMobile ? 'icon' : 'default'}
						onClick={() => setShowRangeFilter(true)}>
						<CalendarIcon className='stroke-popover-1000' />
						{!isMobile && t('elements.buttons.filter')}
					</Button>
				</div>
			</EPageHeader>
			<div
				className={cn(
					'h-full flex flex-col gap-6',
					isMobile && 'gap-4',
				)}>
				{isMobile && (
					<div
						className={cn(
							'-mx-6 px-6 max-w-screen overflow-hidden',
							isMobile && '-mx-4 px-4',
						)}
						ref={emblaRef}>
						<div className='flex gap-2 touch-pan-y'>
							{chartsButtons.map((btn, index) => (
								<Button
									key={index}
									variant={
										activeChart === index
											? 'default'
											: 'outline'
									}
									className={cn(
										`hover:shadow-none ${
											activeChart === index
												? ''
												: 'hover:bg-primary-color-50'
										} `,
										isMobile && 'h-9 px-3.5 py-2',
									)}
									onClick={() => handleChooseChart(index)}>
									<btn.icon
										className={`${
											activeChart === index
												? 'stroke-popover-10'
												: 'stroke-popover-1000'
										}`}
									/>
									{t(btn.name)}
								</Button>
							))}
						</div>
					</div>
				)}
				<div
					className={cn(
						'grid gap-6 grid-cols-3',
						isMobile && 'gap-4 grid-cols-1',
					)}>
					{(!isMobile ||
						(isMobile &&
							activeChart === dashboardChartsButton.Income)) && (
						<DashboardChart
							title='Income'
							chartData={incomeData}
							isLoading={isLoadingIncomeData}
							isMobile={isMobile}
						/>
					)}
					{(!isMobile ||
						(isMobile &&
							activeChart === dashboardChartsButton.ADR)) && (
						<DashboardChart
							title='ADR'
							chartData={ADRData}
							isLoading={isLoadingADRData}
							isMobile={isMobile}
						/>
					)}
					{(!isMobile ||
						(isMobile &&
							activeChart ===
								dashboardChartsButton.OccupancyRate)) && (
						<DashboardChart
							title='Occupancy rate'
							chartData={occupancyRateData}
							isLoading={isLoadingOccupancyRateData}
							isMobile={isMobile}
						/>
					)}
					{((!isMobile && userRole === Roles.admin) ||
						(isMobile &&
							activeChart ===
								dashboardChartsButton.TotalApartments &&
							userRole === Roles.admin)) && (
						<DashboardTotalApartmentsBlock isMobile={isMobile} />
					)}
					{((!isMobile && userRole === Roles.admin) ||
						(isMobile &&
							activeChart === dashboardChartsButton.ManagedBy &&
							userRole === Roles.admin)) && (
						<DashboardManagedByBlock isMobile={isMobile} />
					)}
					{((!isMobile && userRole === Roles.admin) ||
						(isMobile &&
							activeChart === dashboardChartsButton.TotalOwners &&
							userRole === Roles.admin)) && (
						<DashboardTotalOwnersBlock isMobile={isMobile} />
					)}
				</div>
				<DashboardRecentActivities />
				<DashboardNewsAndOffers />
			</div>
			{showRangeFilter && (
				<DashboardPageFilter
					onClose={() => setShowRangeFilter(false)}
					isMobile={isMobile}
				/>
			)}
			<ReleaseNews />
		</section>
	);
};

export default DashboardPage;
